import { useEffect, useState } from "react";
import "../../styles/AdminPanel/DiscountCodeCreate.css";
import { getAdminStores } from "../../api/admin/storeManagement";
import { createDiscount } from "../../api/discount";
import { swalError } from "../../utils/swalAlert";
import * as DatePickerModule from "react-multi-date-picker";

const DatePicker =
  DatePickerModule.default?.default || DatePickerModule.default;
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import moment from "jalali-moment";
const AVATAR_COLORS = [
  "var(--plum-900)",
  "var(--mauve-700)",
  "var(--rose-500)",
];
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const toFa = (n) => String(n).replace(/[0-9]/g, (d) => FA_DIGITS[d]);
const toEn = (s) =>
  String(s).replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)));

function formatFaDate(dateStr) {
  return moment(dateStr, "YYYY-MM-DD").locale("fa").format("jD jMMMM jYYYY");
}

function generateCode() {
  const letters = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  let code = "";
  for (let i = 0; i < 4; i++)
    code += letters[Math.floor(Math.random() * letters.length)];
  for (let i = 0; i < 3; i++)
    code += digits[Math.floor(Math.random() * digits.length)];
  return code;
}

const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const STORES_PER_PAGE = 9;

export default function DiscountCodeCreate() {
  const [selected, setSelected] = useState(new Set());
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(20);
  const [percentText, setPercentText] = useState("20");
  const [expiry, setExpiry] = useState("");
  const [errors, setErrors] = useState({ code: false, expiry: false });
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const fetchStores = async () => {
    setLoading(true);

    try {
      const data = await getAdminStores();
      setStores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("خطا در دریافت لیست فروشگاه‌ها", err);
      swalError({
        title: "خطا در دریافت اطلاعات",
        text:
          err?.detail ||
          err?.error ||
          "خطا در دریافت اطلاعات. لطفا دسترسی خود را بررسی کنید.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const filteredStores = stores.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      s.store_name.toLowerCase().includes(q) ||
      (s.cat || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredStores.length / STORES_PER_PAGE),
  );
  const safePage = Math.min(page, totalPages);
  const pagedStores = filteredStores.slice(
    (safePage - 1) * STORES_PER_PAGE,
    safePage * STORES_PER_PAGE,
  );

  const allFilteredSelected =
    filteredStores.length > 0 &&
    filteredStores.every((s) => selected.has(s.store_id));

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleStore = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        filteredStores.forEach((s) => next.delete(s.store_id));
        return next;
      }
      const next = new Set(prev);
      filteredStores.forEach((s) => next.add(s.store_id));
      return next;
    });
  };

  const goToPage = (p) => {
    setPage(Math.min(Math.max(1, p), totalPages));
  };

  const handleGenerate = () => {
    setCode(generateCode());
    setErrors((e) => ({ ...e, code: false }));
  };

  useEffect(() => {
    setPercentText(toFa(percent));
  }, [percent]);

  const handlePercentTextChange = (e) => {
    const raw = toEn(e.target.value)
      .replace(/[^0-9]/g, "")
      .slice(0, 2);
    setPercentText(toFa(raw));
    if (raw === "") return;
    setPercent(Math.min(90, Number(raw)));
  };

  const handlePercentBlur = () => {
    const n = Number(toEn(percentText));
    if (!n || n < 1) {
      setPercent(1);
    }
  };

  const percentFillRatio = (percent - 1) / (90 - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = { code: !code.trim(), expiry: !expiry };
    setErrors(nextErrors);
    if (nextErrors.code || nextErrors.expiry) return;

    const finalCode = code.trim().toUpperCase();
    const storeIds = Array.from(selected);

    setSubmitting(true);
    try {
      await createDiscount({
        code: finalCode,
        percent,
        expiry,
        storeIds,
      });

      setResult({
        code: finalCode,
        percent,
        expiry,
        stores: stores.filter((s) => selected.has(s.store_id)),
      });
    } catch (err) {
      console.error("خطا در ایجاد کد تخفیف", err);
      swalError({
        title: "خطا در ایجاد کد تخفیف",
        text:
          err?.detail ||
          err?.error ||
          "کد تخفیف ایجاد نشد. لطفا دوباره تلاش کن.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelected(new Set());
    setCode("");
    setPercent(20);
    setPercentText("20");
    setExpiry("");
    setErrors({ code: false, expiry: false });
    setResult(null);
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="disc-page">
      <div className="disc-head">
        <h1>ایجاد کد تخفیف</h1>
      </div>

      {/* فروشگاه‌ها */}
      <section className="disc-card">
        <div className="disc-card-head">
          <div className="disc-titles">
            <h2>فروشگاه‌ها</h2>
            <span>روی هر فروشگاه بزن تا برای این کد فعال یا غیرفعال شود</span>
          </div>
          <label className="disc-select-all">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              ref={(el) =>
                el &&
                (el.indeterminate =
                  filteredStores.some((s) => selected.has(s.store_id)) &&
                  !allFilteredSelected)
              }
              onChange={toggleAll}
            />
            انتخاب همه
            <span className="disc-count">
              {toFa(selected.size)} از {toFa(stores.length)}
            </span>
          </label>
        </div>

        <div className="disc-search-row">
          <svg className="disc-search-icon" viewBox="0 0 20 20" fill="none">
            <circle
              cx="9"
              cy="9"
              r="6.2"
              stroke="currentColor"
              strokeWidth="1.6"
            />
            <path
              d="M17 17L13.5 13.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            className="disc-search-input"
            placeholder="جستجوی فروشگاه بر اساس نام یا دسته‌بندی..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="disc-search-clear"
              onClick={() => setSearch("")}
              aria-label="پاک کردن جستجو"
            >
              ×
            </button>
          )}
        </div>

        <div className="disc-store-grid">
          {pagedStores.length === 0 && (
            <div className="disc-empty-note">
              فروشگاهی با این مشخصات پیدا نشد
            </div>
          )}
          {pagedStores.map((s, i) => {
            const isSelected = selected.has(s.store_id);
            return (
              <div
                key={s.store_id}
                className={`disc-store${isSelected ? " selected" : ""}`}
                onClick={() => toggleStore(s.store_id)}
              >
                <div
                  className="disc-avatar"
                  style={{
                    background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                  }}
                >
                  {s.store_name.trim()[0]}
                </div>
                <div className="disc-meta">
                  <div className="disc-name">{s.store_name}</div>
                  <div className="disc-cat">{s.cat}</div>
                </div>
                <div className="disc-check">
                  <svg viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6.2L4.8 9L10 3"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="disc-pagination">
            <button
              type="button"
              className="disc-page-btn"
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage === 1}
            >
              قبلی
            </button>

            <div className="disc-page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`disc-page-num${p === safePage ? " active" : ""}`}
                  onClick={() => goToPage(p)}
                >
                  {toFa(p)}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="disc-page-btn"
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage === totalPages}
            >
              بعدی
            </button>
          </div>
        )}
      </section>

      {/* جزئیات تخفیف */}
      <section className="disc-card">
        <div className="disc-card-head">
          <div className="disc-titles">
            <h2>جزئیات تخفیف</h2>
            <span>
              کد، درصد تخفیف و تاریخ انقضا را برای فروشگاه‌های انتخاب‌شده تنظیم
              کن
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="disc-details">
            <div className={`disc-field${errors.code ? " has-error" : ""}`}>
              <label htmlFor="discCode">کد تخفیف</label>
              <div className="disc-code-row">
                <input
                  id="discCode"
                  type="text"
                  dir="ltr"
                  placeholder="مثلاً SUMMER30"
                  maxLength={20}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setErrors((er) => ({ ...er, code: false }));
                  }}
                  className={errors.code ? "error" : ""}
                />
                <button
                  type="button"
                  className="disc-btn-ghost"
                  onClick={handleGenerate}
                >
                  پیشنهاد کد
                </button>
              </div>
              {errors.code && (
                <div className="disc-error-msg">
                  کد تخفیف را وارد کن یا از دکمه «پیشنهاد کد» استفاده کن
                </div>
              )}
            </div>

            <div className="disc-field-grid">
              <div className="disc-field">
                <label htmlFor="discPercent">
                  درصد تخفیف{" "}
                  <span className="disc-hint">بین 1 تا 100 درصد</span>
                </label>
                <div className="disc-percent-row">
                  <input
                    id="discPercent"
                    type="range"
                    min="1"
                    max="100"
                    value={percent}
                    style={{ "--fill-ratio": percentFillRatio }}
                    onChange={(e) => setPercent(Number(e.target.value))}
                  />
                  <label className="disc-percent-badge">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={percentText}
                      onChange={handlePercentTextChange}
                      onBlur={handlePercentBlur}
                      maxLength={2}
                      aria-label="درصد تخفیف"
                    />
                    <span>٪</span>
                  </label>
                </div>
              </div>

              <div className={`disc-field${errors.expiry ? " has-error" : ""}`}>
                <label htmlFor="discExpiry">تاریخ انقضا</label>
                <DatePicker
                  id="discExpiry"
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  value={
                    expiry
                      ? new DateObject({
                          date: expiry,
                          format: "YYYY-MM-DD",
                          calendar: gregorian,
                          locale: gregorian_en,
                        }).convert(persian, persian_fa)
                      : null
                  }
                  onChange={(dateObject) => {
                    if (!dateObject) {
                      setExpiry("");
                      return;
                    }
                    const iso = dateObject
                      .convert(gregorian, gregorian_en)
                      .format("YYYY-MM-DD");
                    setExpiry(iso);
                    setErrors((er) => ({ ...er, expiry: false }));
                  }}
                  minDate={new DateObject({
                    date: tomorrowISO(),
                    format: "YYYY-MM-DD",
                    calendar: gregorian,
                    locale: gregorian_en,
                  }).convert(persian, persian_fa)}
                  format="YYYY/MM/DD"
                  editable={false}
                  inputClass={
                    "disc-date-input" + (errors.expiry ? " error" : "")
                  }
                  containerClassName="profile-jalali-container"
                  placeholder="انتخاب تاریخ انقضا"
                />

                {errors.expiry && (
                  <div className="disc-error-msg">
                    تاریخ انقضا را انتخاب کن؛ باید بعد از امروز باشد
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="disc-submit-row">
            <button
              type="submit"
              className="disc-btn-primary"
              disabled={submitting}
            >
              {submitting ? "در حال ایجاد..." : "ایجاد کد تخفیف"}
            </button>
          </div>
        </form>
      </section>

      {/* نتیجه */}
      {result && (
        <section className="disc-card disc-result">
          <div className="disc-card-head" style={{ marginBottom: 0 }}>
            <div className="disc-titles">
              <h2>کد تخفیف ساخته شد</h2>
              <span>این کد برای فروشگاه‌های زیر تا تاریخ انقضا فعال است</span>
            </div>
          </div>

          <div className="disc-result-code">
            <span className="disc-code-val">{result.code}</span>
            <button type="button" onClick={handleCopy}>
              {copied ? "کپی شد" : "کپی کد"}
            </button>
          </div>

          <div className="disc-result-summary">
            <div className="disc-item">
              <span>درصد تخفیف</span>
              <strong>{toFa(result.percent)}٪</strong>
            </div>
            <div className="disc-item">
              <span>تاریخ انقضا</span>
              <strong>{formatFaDate(result.expiry)}</strong>
            </div>
          </div>

          <div className="disc-item">
            <span style={{ marginBottom: 8, display: "block" }}>
              فروشگاه‌های فعال
            </span>
            <div className="disc-chip-list">
              {result.stores.map((s) => (
                <span className="disc-chip" key={s.store_id}>
                  {s.store_name}
                </span>
              ))}
            </div>
          </div>

          <div className="disc-result-actions">
            <button
              type="button"
              className="disc-btn-link"
              onClick={handleReset}
            >
              ساخت کد تخفیف جدید
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
